import {Icon, Spinner, SpinnerSize, Tab} from "@blueprintjs/core";
import { useApiEffect } from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";

import { DysplasiaRequestApi, ERequestType, RequestApi, ERequestState } from "../../../../api";
import { ERoutes } from "../../../../ERoutes";
import {
  BackButton,
  ConditionTab,
  PageBase,
  ProtectedTabs,
  RequestHistoryTab,
  RequestStickyMenu,
  VerificationTab,
} from "../../../../components";
import { useGlobalData, useEventsContext } from "../../../../contexts";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { DysplasiaRequestDetail } from "./DysplasiaRequestDetail";
import { getRouteStr } from "../../../../utils/RequestRouteUtil";
import { getSearchRouteStr } from "../../../../utils/RequestSearchRouteUtil";
import { LinkedSubFilesTab } from "../../../../components";
import { BillTab } from "../../BillTab";
import { MailLogListPage } from "../../../mail-log/MailLogListPage";

export interface IDysplasiaRequestItemPageProps {}

export const DysplasiaRequestItemPage: React.FunctionComponent<IDysplasiaRequestItemPageProps> = (props) => {
  const {
    id,
    tab,
    state,
    masterRequestId = "0",
  } = useParams<{
    id: string;
    tab: string;
    state: string;
    masterRequestId: string;
  }>();

  const { t } = useTl();
  const history = useHistory();
  const api = useApiService(DysplasiaRequestApi);
  const requestApi = useApiService(RequestApi);
  const { currentLanguage } = useGlobalData();
  const { dispatchEvent } = useEventsContext();

  const [requestInfo, requestInfoLoading, , refreshRequestInfo] = useApiEffect(
    async () =>
      await requestApi.getRequestInfo(ERequestType.DysplasiaRequest, +id, +masterRequestId, currentLanguage.languageId),
    [requestApi, id, masterRequestId, currentLanguage],
  );

  const refreshRequest = React.useCallback(() => {
    refreshRequestInfo();
  }, [refreshRequestInfo]);

  const closeRequest = React.useCallback(async () => {
    await api.close(requestInfo?.parentRequestId);
    dispatchEvent("RequestRefresh");
  }, [api, dispatchEvent, requestInfo]);

  const breadCrumbs = React.useMemo(() => {
    let bc = [
      { text: t(ETLCodes.DysplasiasRequests), route: getSearchRouteStr(requestInfo?.requestTypeCode) },
      {
        text: requestInfoLoading ? (
          <Spinner size={SpinnerSize.SMALL} />
        ) : +requestInfo?.requestId === 0 ? (
          t(ETLCodes.New)
        ) : (
          requestInfo.requestNumber
        ),
      },
    ];

    if (requestInfo?.masterRequestId > 0) {
      bc = [
        {
          text: requestInfo.masterRequestDisplayName,
          route: getSearchRouteStr(requestInfo.masterRequestTypeCode),
        },
        {
          text: requestInfo.masterRequestNumber,
          route: getRouteStr(requestInfo.masterRequestTypeCode, requestInfo.masterSubRequestId, "view"),
        },
        ...bc,
      ];
    }
    return bc;
  }, [requestInfo, requestInfoLoading, t]);

  return (
    <PageBase
      breadCrumbs={breadCrumbs}
      actionHistory={
        requestInfo != null && {
          tlCode: ETLCodes.ActionHistoryDysplasiaRequest,
          name: requestInfo.requestNumber,
          id: +id,
        }
      }
      stickyContent={() => {
        if (requestInfo?.requestId > 0 || requestInfo?.masterRequestId > 0) {
          return (
            <RequestStickyMenu
              requestId={requestInfo.masterRequestId > 0 ? requestInfo.masterRequestId : requestInfo.parentRequestId}
              requestInfo={requestInfo}
              refreshFunc={refreshRequest}
              closeFunc={closeRequest}
            />
          );
        } else {
          return null;
        }
      }}
    >
      <ProtectedTabs
        id="TabsDysplasiaRequest"
        onChange={(newTabId) => history.push(`${ERoutes.dysplasiaRequest}/${+id}/${newTabId}/${state}`)}
        selectedTabId={tab}
        renderActiveTabPanelOnly
      >
        <BackButton backRoute={ERoutes.dysplasiaRequest} />
        <Tab
          id="detail"
          title={
            <div>
              {t(ETLCodes.Detail)} {requestInfo?.hasWarning && <Icon icon="warning-sign" intent="danger" />}
            </div>
          }
          panel={
            <DysplasiaRequestDetail dysplasiaRequestId={+id} editMode={state === "edit"} requestInfo={requestInfo} />
          }
        />
        <Tab
          id="verification"
          title={t(ETLCodes.Verifications)}
          panel={
            requestInfoLoading ? (
              <Spinner />
            ) : (
              <VerificationTab
                requestId={requestInfo.parentRequestId}
                editMode={state === "edit"}
                statusETLCode={ETLCodes.VerificationsToObtainBilledStatus}
              />
            )
          }
          disabled={+id < 1}
        />
        <Tab
          id="conditions"
          title={t(ETLCodes.Conditions)}
          disabled={+id <= 0}
          panel={
            requestInfo ? (
              <ConditionTab
                requestId={requestInfo.parentRequestId}
                requestState={requestInfo.requestStateCode}
                editMode={state === "edit"}
                statusETLCode={ETLCodes.Conditions}
              />
            ) : null
          }
        />
        <Tab
          id="subrequests"
          title={t(ETLCodes.SubRequests, {
            count: requestInfoLoading ? `` : `(${requestInfo.subRequestCount})`,
          })}
          panel={
            requestInfoLoading ? (
              <Spinner />
            ) : (
              <LinkedSubFilesTab requestInfo={requestInfo} editMode={state === "edit"} />
            )
          }
          disabled
        />
        <Tab
          id="billing"
          title={t(ETLCodes.Billing)}
          panel={
            <BillTab
              requestId={requestInfo?.parentRequestId}
              disabled={requestInfo?.requestStateCode !== ERequestState.CANCELLED}
            />
          }
          disabled={requestInfo?.requestStateCode === ERequestState.REQUESTED}
        />
        <Tab
          id="history"
          title={t(ETLCodes.History)}
          panel={requestInfoLoading ? <Spinner /> : <RequestHistoryTab requestId={requestInfo.parentRequestId} />}
          disabled={+id <= 0}
        />
        <Tab
          id="mailing"
          title={t(ETLCodes.Email)}
          panel={requestInfoLoading ? <Spinner /> : <MailLogListPage requestId={requestInfo?.parentRequestId} />}
          disabled={+id <= 0}
        />
      </ProtectedTabs>
    </PageBase>
  );
};
